import { Moment } from 'moment'
import {
  CustomFieldDataType,
  CustomListItemStyle,
  Report,
} from '@cims-online/shared'
import { toMoment } from '../../../../utils/toMoment'
import { SEPARATOR } from '../../../CIMSSelect/common/types'
import { LabeledValueType } from '../../../CIMSSelect/types'
import { toLabeledValues } from '../../../CIMSSelect/utils/labeledValues'
import {
  MetadataListFormItemsEntities,
  MetadataListFormItemsReports,
} from '../../CustomFieldEditor/CustomFieldRow/MetadataListFormItems/types'
import { SelectCustomFieldEditorValues } from '../../CustomFieldEditor/types'
import {
  SimpleRecord,
  LinkLike,
  MetadataRecord,
  SelectRecord,
} from '../../types'
import { ProjectCustomFieldsContext } from '../types'

/**
 * @see {@link ProjectCustomFieldsContext.toCustomFieldEditorValue}
 */
export const toCustomFieldEditorValue: ProjectCustomFieldsContext['toCustomFieldEditorValue'] =
  (customFields) => {
    if (!customFields) return undefined
    const values = {
      ...customFields.entityValues.reduce<MetadataRecord>(
        (record, { customFieldId, entities }) => {
          record[customFieldId] = {
            dataTypeId: CustomFieldDataType.Entity.id,
            entities: entities.reduce<MetadataListFormItemsEntities>(
              (record, value) => {
                record[value.entityId] = linkLikeLabeledValue(value)
                return record
              },
              Object()
            ),
          }
          return record
        },
        Object()
      ),
      ...customFields.reportValues.reduce<MetadataRecord>(
        (record, { customFieldId, reports }) => {
          record[customFieldId] = {
            dataTypeId: CustomFieldDataType.Report.id,
            reports: reports.reduce<MetadataListFormItemsReports>(
              (record, { label, reportId, recordId }) => {
                const { classType } = Report.fromId(reportId) ?? {}
                const value = recordId
                  ? `${classType}${SEPARATOR}${recordId}`
                  : null
                /**
                 * null works and is supported by ant design, but the interface
                 * for {@link RawValue}, does not include it
                 */
                record[reportId] = { label, value: value! }
                return record
              },
              Object()
            ),
          }
          return record
        },
        Object()
      ),
      ...customFields.listValues.checkboxes.reduce<SimpleRecord<string[]>>(
        (record, { booleans, customFieldId }) => {
          record[customFieldId] = {
            dataTypeId: CustomFieldDataType.List.id,
            styleId: CustomListItemStyle.Checkboxes.id,
            value: booleans.reduce<string[]>(
              (values, { customListItemId, checked }) => {
                if (customListItemId && checked) values.push(customListItemId)
                return values
              },
              Array<string>()
            ),
          }
          return record
        },
        Object()
      ),
      ...customFields.listValues.singles.reduce<SelectRecord>(
        (record, { customFieldId, customListItem }) => {
          if (!customListItem) return record
          const { id, value } = customListItem
          record[customFieldId] = {
            dataTypeId: CustomFieldDataType.List.id,
            value: { value: id, label: value },
            styleId: CustomListItemStyle.Single.id,
          }
          return record
        },
        Object()
      ),
      ...customFields.listValues.multiples.reduce<
        SelectRecord<SelectCustomFieldEditorValues>
      >((record, { customFieldId, customListItems }) => {
        record[customFieldId] = {
          dataTypeId: CustomFieldDataType.List.id,
          value: toLabeledValues(customListItems, {
            deleted: ({ deleted }) => deleted,
            value: 'id',
            label: 'value',
          }),
          styleId: CustomListItemStyle.Multiple.id,
        }
        return record
      }, Object()),
      ...customFields.dateValues.reduce<SimpleRecord<Moment>>(
        (record, { customFieldId, value, metadata }) => {
          record[customFieldId] = {
            dataTypeId: CustomFieldDataType.Date.id,
            value: toMoment(value),
            timestamp: metadata?.timestamp,
          }
          return record
        },
        Object()
      ),
      ...simpleReduce(customFields.booleanValues),
      ...simpleReduce(customFields.numberValues),
      ...simpleReduce(customFields.textValues),
    }
    return values
  }

function linkLikeLabeledValue({
  label,
  recordId,
}: LinkLike<unknown>): LabeledValueType {
  return { label, value: recordId! }
}

function simpleReduce<T>(values: { customFieldId: string; value?: T }[]) {
  return values.reduce<SimpleRecord<T>>((record, { customFieldId, value }) => {
    if (typeof value === 'string')
      record[customFieldId] = {
        dataTypeId: CustomFieldDataType.Text.id,
        value,
      }
    else if (typeof value === 'boolean')
      record[customFieldId] = {
        dataTypeId: CustomFieldDataType.Boolean.id,
        value,
      }
    else if (typeof value === 'number')
      record[customFieldId] = {
        dataTypeId: CustomFieldDataType.Number.id,
        value,
      }
    return record
  }, Object())
}
